import {
    GET_DAILY_METRICS,
    GET_FILTER_OPTION_DATA,
    GET_TOP_ASSISTANCE_REASONS,
    SMART_INBOX_EMAIL_CATEGORY_DISTRIBUTION,
    SMART_INBOX_PERFORMANCE_METRICS,
    SMART_INBOX_SHARE,
} from '../queries';
import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import alphaNumericSorter from '../../core/utils/alphaNumericSorter.ts';
import { useTranslation } from 'react-i18next';
import { format, subDays, subMonths, subWeeks, subYears } from 'date-fns';
import { GET_SMART_INBOX_INTEGRATIONS } from '../../customer/queries.ts';
import { PERSONAL_SMART_INBOX_INTEGRATIONS } from '../../users/queries.ts';
import { isoDateFormat } from '../../core/utils/dates.ts';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import { canSeeDocuments } from '../../users/utils.ts';

// just something in case no document type selected yet
// Use Faked data for the demo presentation
import { navigationDocumentTypes } from '../../core/components/Navigation/internal/useNavigation.ts';
import { mapDocumentTypeNameToGqlDocumentType } from '../../document/constants.ts';
import { demoGenerateAssistanceReasons, demoGenerateDailyMetrics } from './demoDataUtils.ts';
import { useDataRetentionMinDate } from './dataRetention.ts';

export interface IDailyMetric {
    date: string;
    finishedCount: number;
    assistedCount: number;
    discardedCount: number;
    deletedCount: number;
    automatedCount: number;
    headerAutomatedCount: number;
    headerAssistedCount: number;
    headerTotalCount: number;
    headerAutomationRatio: number;
    lineItemsAutomatedCount: number;
    lineItemsAssistedCount: number;
    lineItemsTotalCount: number;
    lineItemsAutomationRatio: number;
    medianAssistanceTime?: number;
    totalAssistanceTime?: number;
    totalAssistanceTimeBeforeWorkist?: number;
    averageAssistanceTimePerDocumentBeforeWorkist?: number;
    trackedCount?: number;
}

export interface IAssistanceReason {
    reason: string;
    fieldName?: string;
    percentage: number;
}

const DATA_BY_DOCUMENT_TYPE = {
    rfq: {
        channels: 'rfqProcessingChannels',
    },
    order: {
        channels: 'orderProcessingChannels',
    },
    orderConfirmation: {
        channels: 'orderConfirmationProcessingChannels',
    },
    invoice: {
        channels: 'invoiceProcessingChannels',
    },
    deliveryNote: {
        channels: 'deliveryNoteProcessingChannels',
    },
    universal: {
        channels: 'universalProcessingChannels',
    },
};

export const useFilterOptions = () => {
    const { t } = useTranslation('analytics');
    const { user } = useApplicationContext();

    // the channel lists and the user list both need the documents permission
    const { data, loading, error } = useQuery(GET_FILTER_OPTION_DATA, { skip: !canSeeDocuments(user) });
    const dataRetentionMinDate = useDataRetentionMinDate();

    const channelOptionsByDocumentType: any = {};

    const users = data?.users?.users;
    const customer = data?.viewer?.customer;

    const getChannelOptions = (documentType) => {
        const channelsKey = DATA_BY_DOCUMENT_TYPE[documentType]?.channels;
        const channels = channelsKey ? [...(data?.[channelsKey] || [])] : [];
        channels.sort((a: any, b: any) => alphaNumericSorter(a.name, b.name));
        return (
            channels?.map((channel) => ({
                label: channel.name,
                value: channel.id,
                automationEnabled: channel.automationEnabled,
                groupName: channel.automationEnabled ? 'automationEnabled' : 'automationDisabled',
            })) || []
        );
    };

    const userOptions =
        users?.map((user) => ({
            label: `${user.firstName} ${user.lastName}`,
            value: user.id,
        })) || [];

    const documentTypeOptions = [];

    documentTypeOptions.push({
        label: t('dashboard.filters.documentType.rfq'),
        value: 'rfq',
        icon: navigationDocumentTypes.rfq.icon,
        disabled: !customer?.isRfqsEnabled,
    });
    channelOptionsByDocumentType['rfq'] = getChannelOptions('rfq');

    documentTypeOptions.push({
        label: t('dashboard.filters.documentType.order'),
        value: 'order',
        icon: navigationDocumentTypes.order.icon,
        disabled: !customer?.isOrdersEnabled,
    });
    channelOptionsByDocumentType['order'] = getChannelOptions('order');

    documentTypeOptions.push({
        label: t('dashboard.filters.documentType.orderConfirmation'),
        value: 'orderConfirmation',
        icon: navigationDocumentTypes.orderConfirmation.icon,
        disabled: !customer?.isOrderConfirmationsEnabled,
    });
    channelOptionsByDocumentType['orderConfirmation'] = getChannelOptions('orderConfirmation');

    documentTypeOptions.push({
        label: t('dashboard.filters.documentType.invoice'),
        value: 'invoice',
        icon: navigationDocumentTypes.invoice.icon,
        disabled: !customer?.isInvoicesEnabled,
    });
    channelOptionsByDocumentType['invoice'] = getChannelOptions('invoice');

    documentTypeOptions.push({
        label: t('dashboard.filters.documentType.deliveryNote'),
        value: 'deliveryNote',
        icon: navigationDocumentTypes.deliveryNote.icon,
        disabled: !customer?.isDeliveryNotesEnabled,
    });
    channelOptionsByDocumentType['deliveryNote'] = getChannelOptions('deliveryNote');

    documentTypeOptions.push({
        label: t('dashboard.filters.documentType.universal'),
        value: 'universal',
        icon: navigationDocumentTypes.universal.icon,
        disabled: !customer?.isUniversalEnabled,
    });
    channelOptionsByDocumentType['universal'] = getChannelOptions('universal');

    const today = new Date();

    let dateRangeOptions = [
        {
            label: t('dashboard.filters.dateRange.last7Days'),
            labelShort: t('dashboard.filters.dateRange.last7DaysShort'),
            value: [format(subWeeks(today, 1), isoDateFormat), format(today, isoDateFormat)].join(','),
        },
        {
            label: t('dashboard.filters.dateRange.last30Days'),
            labelShort: t('dashboard.filters.dateRange.last30DaysShort'),
            value: [format(subMonths(today, 1), isoDateFormat), format(today, isoDateFormat)].join(','),
        },
        {
            label: t('dashboard.filters.dateRange.last3Months'),
            labelShort: t('dashboard.filters.dateRange.last3MonthsShort'),
            value: [format(subDays(today, 90), isoDateFormat), format(today, isoDateFormat)].join(','),
        },
        {
            label: t('dashboard.filters.dateRange.last6Months'),
            labelShort: t('dashboard.filters.dateRange.last6MonthsShort'),
            value: [format(subMonths(today, 6), isoDateFormat), format(today, isoDateFormat)].join(','),
        },
        {
            label: t('dashboard.filters.dateRange.last12Months'),
            labelShort: t('dashboard.filters.dateRange.last12MonthsShort'),
            value: [format(subYears(today, 1), isoDateFormat), format(today, isoDateFormat)].join(','),
        },
    ];

    if (dataRetentionMinDate) {
        // clamp presets that reach past the retention window to the oldest selectable date and
        // drop the ones that collapse into an identical range
        const minDate = format(dataRetentionMinDate, isoDateFormat);
        const seenValues = new Set<string>();
        dateRangeOptions = dateRangeOptions
            .map((option) => {
                const [startDate, endDate] = option.value.split(',');
                return startDate < minDate ? { ...option, value: [minDate, endDate].join(',') } : option;
            })
            .filter((option) => {
                if (seenValues.has(option.value)) return false;
                seenValues.add(option.value);
                return true;
            });
    }

    return {
        loading,
        error: !!error,

        documentType: documentTypeOptions,
        channelsByDocumentType: channelOptionsByDocumentType,
        dateRange: dateRangeOptions,
        user: userOptions,
    };
};

export const useDailyMetrics = ({
    documentType,
    dateRange,
    channels,
    isDemo = false,
}: {
    documentType: string;
    dateRange?: string[];
    channels?: string[];
    isDemo?: boolean;
}) => {
    const { t, i18n } = useTranslation('analytics');

    const [startDate, endDate] = dateRange;

    const query = useQuery(GET_DAILY_METRICS, {
        variables: {
            documentType: documentType ? mapDocumentTypeNameToGqlDocumentType[documentType] : documentType,
            startDate,
            endDate,
            channelIds: channels,
        },
        skip: !documentType || isDemo,
    });

    if (isDemo) {
        const demoData = documentType ? demoGenerateDailyMetrics(documentType, startDate, endDate, channels) : [];
        return {
            data: demoData,
            loading: !documentType,
            isError: false,
        };
    }

    return {
        data: (query.data?.dailyMetrics || []) as IDailyMetric[],
        loading: query.loading,
        isError: Boolean(query.error),
    };
};

export const useAssistanceReasons = ({
    documentType,
    dateRange,
    channels,
    isDemo = false,
}: {
    documentType: string;
    dateRange?: string[];
    channels?: string[];
    users?: string[];
    isDemo?: boolean;
}) => {
    const [startDate, endDate] = dateRange;

    const {
        data: data,
        loading,
        error,
    } = useQuery(GET_TOP_ASSISTANCE_REASONS, {
        variables: {
            documentType: documentType ? mapDocumentTypeNameToGqlDocumentType[documentType] : documentType,
            startDate,
            endDate,
            channelIds: channels,
        },
        skip: !documentType,
    });

    if (isDemo) {
        const demoData = documentType ? demoGenerateAssistanceReasons(documentType, startDate, endDate, channels) : [];
        return {
            data: demoData,
            loading: !documentType,
            isError: false,
        };
    }

    return {
        data: data?.topAssistanceReasons || [],
        loading,
        isError: Boolean(error),
    };
};

export const useSmartInboxShare = ({ dateRange }: { dateRange?: string[] }) => {
    const [startDate, endDate] = dateRange || [];
    const { data, loading, error } = useQuery(SMART_INBOX_SHARE, {
        variables: { startDate, endDate },
        skip: !startDate || !endDate,
    });
    return { data: data?.smartInboxShare || null, loading, isError: Boolean(error) };
};

export const useSmartInboxShareOverTime = ({ dateRange }: { dateRange?: string[] }) => {
    const [startDate, endDate] = dateRange || [];
    const { data, loading, error } = useQuery(SMART_INBOX_SHARE, {
        variables: { startDate, endDate },
        skip: !startDate || !endDate,
    });
    return { data: data?.smartInboxShare?.daily || [], loading, isError: Boolean(error) };
};

export const useSmartInboxPerformanceMetrics = ({
    integrationIds,
    dateRange,
}: {
    integrationIds: string[];
    dateRange?: string[];
}) => {
    const [startDate, endDate] = dateRange || [];
    const { data, loading, error } = useQuery(SMART_INBOX_PERFORMANCE_METRICS, {
        variables: { integrationIds, startDate, endDate },
        skip: !integrationIds?.length || !startDate || !endDate,
    });
    return { data: data?.smartInboxPerformanceMetrics || null, loading, isError: Boolean(error) };
};

export const useSmartInboxEmailCategories = ({
    integrationIds,
    dateRange,
}: {
    integrationIds: string[];
    dateRange?: string[];
}) => {
    const [startDate, endDate] = dateRange || [];
    const { data, loading, error } = useQuery(SMART_INBOX_EMAIL_CATEGORY_DISTRIBUTION, {
        variables: { integrationIds, startDate, endDate },
        skip: !integrationIds?.length || !startDate || !endDate,
    });
    return { data: data?.smartInboxEmailCategoryDistribution?.categories || [], loading, isError: Boolean(error) };
};

export const useSmartInboxEmailCategoriesOverTime = ({
    integrationIds,
    dateRange,
}: {
    integrationIds: string[];
    dateRange?: string[];
}) => {
    const [startDate, endDate] = dateRange || [];
    const { data, loading, error } = useQuery(SMART_INBOX_EMAIL_CATEGORY_DISTRIBUTION, {
        variables: { integrationIds, startDate, endDate },
        skip: !integrationIds?.length || !startDate || !endDate,
    });
    return { data: data?.smartInboxEmailCategoryDistribution?.daily || [], loading, isError: Boolean(error) };
};

export const useSmartInboxTopEmailCategories = ({
    integrationIds,
    dateRange,
    limit,
}: {
    integrationIds: string[];
    dateRange?: string[];
    limit?: number;
}) => {
    const [startDate, endDate] = dateRange || [];
    const { data, loading, error } = useQuery(SMART_INBOX_EMAIL_CATEGORY_DISTRIBUTION, {
        variables: { integrationIds, startDate, endDate, limit },
        skip: !integrationIds?.length || !startDate || !endDate,
    });
    return { data: data?.smartInboxEmailCategoryDistribution?.topCategories || [], loading, isError: Boolean(error) };
};

export type SmartInboxIntegrationOption = { value: string; label: string; groupName: 'personal' | 'shared' };

export const useSmartInboxIntegrations = () => {
    const { data: sharedData, loading: sharedLoading } = useQuery(GET_SMART_INBOX_INTEGRATIONS);
    const { data: personalData, loading: personalLoading } = useQuery(PERSONAL_SMART_INBOX_INTEGRATIONS);

    const integrationOptions = useMemo((): SmartInboxIntegrationOption[] => {
        const personal = (personalData?.personalSmartInboxIntegrations || []).map(
            (i: { id: string; mailInbox: string }) => ({
                value: i.id,
                label: i.mailInbox,
                groupName: 'personal' as const,
            })
        );
        const shared = (sharedData?.smartInboxIntegrations || []).map((i: { id: string; mailInbox: string }) => ({
            value: i.id,
            label: i.mailInbox,
            groupName: 'shared' as const,
        }));
        return [...personal, ...shared];
    }, [personalData, sharedData]);

    const allIntegrationIds = useMemo(() => integrationOptions.map((o) => o.value), [integrationOptions]);

    return { integrationOptions, allIntegrationIds, loading: sharedLoading || personalLoading };
};
